import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, ArrowRight, Play, CheckCircle2, Mail, Clock,
  Users, Settings, TrendingUp, HandCoins, Headset, ShieldCheck,
  PenLine, PhoneCall, MapPin, FileText, Wrench, BadgeCheck, BriefcaseBusiness,
} from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import VideoDialog from "@/components/VideoDialog";
import { WHY_PARTNER, ADVANTAGE_POINTS, JOURNEY_STEPS, CONTACT_INFO } from "@/lib/constants";
import { useEnquiry } from "@/lib/enquiry-context";

const ICONS = {
  Users, Settings, TrendingUp, HandCoins, Headset, ShieldCheck,
  PenLine, PhoneCall, MapPin, FileText, Wrench,
};

const HERO_MINI = [
  { icon: BadgeCheck, label: ["Company", "Operated"] },
  { icon: Settings, label: ["Proven", "Operating System"] },
  { icon: TrendingUp, label: ["High Demand", "Category"] },
];

export default function FranchisePage() {
  const { openBook } = useEnquiry();
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <main data-testid="franchise-page" className="bg-[#f7f2e8]">
      {/* Hero */}
      <section className="relative bg-[#070604] overflow-hidden pt-[72px] md:pt-20">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 py-14 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <FadeIn>
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.24em] uppercase text-[#c9a14a]">
              <Link to="/" className="text-[#f7f2e8]/60 hover:text-[#c9a14a] transition-colors">Home</Link>
              <ChevronRight size={11} className="text-[#f7f2e8]/40" />
              <span>Franchise Opportunity</span>
            </div>
            <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.06]">
              <span className="block text-[#f7f2e8]">Partner With a Brand</span>
              <span className="block text-[#c9a14a]">Built for the Future.</span>
            </h1>
            <p className="mt-6 text-[#f7f2e8]/80 text-base md:text-lg leading-relaxed max-w-lg">
              Carlton Wellness Center is India's premium protocol-led wellness network.
              Company-operated. System-driven. Investor-friendly. Built for scale.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-5" data-testid="franchise-hero-minis">
              {HERO_MINI.map(({ icon: Icon, label }, i) => (
                <div key={label[0]} className={`flex items-center gap-3 ${i > 0 ? "sm:pl-8 sm:border-l sm:border-[#c9a14a]/25" : ""}`}>
                  <span className="w-10 h-10 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#c9a14a]">
                    <Icon size={17} strokeWidth={1.5} />
                  </span>
                  <p className="text-[12px] text-[#f7f2e8]/90 leading-snug">{label[0]}<br />{label[1]}</p>
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <button onClick={() => openBook("franchise")} data-testid="franchise-hero-enquire-button" className="btn-gold">
                Enquire Now <ArrowRight size={15} strokeWidth={2} />
              </button>
              <button
                onClick={() => setVideoOpen(true)}
                data-testid="franchise-watch-overview-button"
                className="flex items-center gap-3 text-[11px] font-bold tracking-[0.18em] uppercase text-[#f7f2e8] hover:text-[#c9a14a] transition-colors"
              >
                <span className="w-11 h-11 rounded-full border border-[#c9a14a]/60 flex items-center justify-center text-[#c9a14a]">
                  <Play size={14} fill="currentColor" strokeWidth={0} className="ml-0.5" />
                </span>
                Watch Franchise Overview
              </button>
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="rounded-2xl overflow-hidden shadow-2xl">
            <img src="/assets/site/franchise-hero.jpg" alt="Carlton Wellness Center lobby" data-testid="franchise-hero-image" className="w-full aspect-[4/3] object-cover" />
          </FadeIn>
        </div>
      </section>

      {/* Why Partner */}
      <section id="why-partner" data-testid="franchise-why-partner" className="bg-[#f7f2e8]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-center text-[#141414]">
              Why Partner With <span className="text-[#c9a14a]">Carlton Wellness Center?</span>
            </h2>
          </FadeIn>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-y-12">
            {WHY_PARTNER.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <FadeIn key={w.title} delay={i * 0.07} className={`text-center px-5 ${i > 0 ? "xl:border-l xl:border-[#c9a14a]/20" : ""}`}>
                  <span className="mx-auto w-14 h-14 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#a8813a]">
                    <Icon size={24} strokeWidth={1.3} />
                  </span>
                  <p className="mt-5 font-semibold text-[#141414] leading-snug">{w.title}</p>
                  <p className="mt-2 text-[12px] text-[#4a4a4a] leading-relaxed">{w.desc}</p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Carlton Advantage */}
      <section data-testid="franchise-advantage" className="relative bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto grid lg:grid-cols-2 items-stretch">
          <FadeIn className="px-5 md:px-10 py-16 md:py-24">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#f7f2e8]">
              The Carlton <span className="text-[#c9a14a]">Advantage</span>
            </h2>
            <p className="mt-6 text-[#f7f2e8]/70 text-base leading-relaxed max-w-md">
              We bring together science, tradition, hospitality and technology to deliver
              measurable wellness outcomes in a premium environment.
            </p>
            <ul className="mt-9 space-y-4">
              {ADVANTAGE_POINTS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-[14px] text-[#f7f2e8]/90">
                  <CheckCircle2 size={17} className="text-[#c9a14a] shrink-0" strokeWidth={1.6} />
                  {p}
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.1} className="relative min-h-[300px]">
            <img src="/assets/site/carlton-advantage.jpg" alt="Carlton Wellness Center advantage" className="absolute inset-0 w-full h-full object-cover" />
          </FadeIn>
        </div>
      </section>

      {/* Partnership Journey */}
      <section data-testid="franchise-journey" className="bg-[#f2ede3]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn className="text-center">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#141414]">
              Our Partnership <span className="text-[#c9a14a]">Journey</span>
            </h2>
            <p className="mt-4 text-sm text-[#4a4a4a]">
              A simple, transparent process designed for clarity and confidence.
            </p>
          </FadeIn>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-12 relative">
            {JOURNEY_STEPS.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <FadeIn key={s.title} delay={i * 0.08} className="text-center relative">
                  <div className="relative mx-auto w-24 h-24">
                    <span className="w-24 h-24 rounded-full border border-[#c9a14a]/50 bg-[#f7f2e8] flex items-center justify-center text-[#a8813a] mx-auto">
                      <Icon size={30} strokeWidth={1.2} />
                    </span>
                    <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-[#c9a14a] text-[#050505] text-[12px] font-bold flex items-center justify-center border-2 border-[#f2ede3]">
                      {i + 1}
                    </span>
                  </div>
                  <p className="mt-7 font-semibold text-[#141414]">{s.title}</p>
                  <p className="mt-2 text-[12px] text-[#4a4a4a] leading-relaxed px-2">{s.desc}</p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA + Franchise team */}
      <section id="franchise-team" data-testid="franchise-cta" className="relative bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <img
          src="/assets/site/franchise-lobby.jpg"
          alt=""
          aria-hidden="true"
          className="absolute right-0 top-0 h-full w-1/3 object-cover opacity-25 hidden lg:block"
        />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <FadeIn>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.1]">
              <span className="text-[#f7f2e8]">Let's Build the Future</span>
              <br />
              <span className="text-[#f7f2e8]">of Wellness. </span>
              <span className="text-[#c9a14a]">Together.</span>
            </h2>
            <p className="mt-6 text-[#f7f2e8]/75 text-base leading-relaxed max-w-md">
              Join India's most trusted wellness network and create a legacy of impact,
              wellness and long-term returns.
            </p>
            <button onClick={() => openBook("franchise")} data-testid="franchise-cta-enquire-button" className="btn-gold mt-8">
              Enquire Now <ArrowRight size={15} strokeWidth={2} />
            </button>
          </FadeIn>
          <FadeIn delay={0.12} className="border border-[#c9a14a]/35 bg-[#0b0b0b]/80 backdrop-blur p-8 md:p-10" data-testid="franchise-team-card">
            <p className="font-serif-display text-2xl text-[#c9a14a]">Speak to Our Franchise Team</p>
            <div className="mt-7 space-y-5">
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-4 text-[#f7f2e8] hover:text-[#c9a14a] transition-colors" data-testid="franchise-team-email">
                <Mail size={17} className="text-[#c9a14a]" /> {CONTACT_INFO.email}
              </a>
              <p className="flex items-center gap-4 text-[#f7f2e8]/85">
                <Clock size={17} className="text-[#c9a14a]" /> {CONTACT_INFO.hours}
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <VideoDialog open={videoOpen} onOpenChange={setVideoOpen} />
    </main>
  );
}
